/* color-pref.js — the shopper's preferred colour opens the page.
 *
 * The offline recommendation engine knows each customer's preferred colour;
 * the color-pref service joins it to our visitor id off the request path.
 * On a PDP this asks once (six-hour cache, same shape as purchase-info),
 * and if the product's Color option carries one of the shopper's colours,
 * SELECTS it — the theme's own picker then does everything else: gallery,
 * price, SKU, and the media card all follow variant:change.
 *
 * Polite by design:
 *   • a URL that already names a variant is a choice — never overridden
 *   • one attempt per pageview, before the shopper has touched the picker
 *   • unknown visitor / unreachable service / no matching colour = nothing
 *   • kill switch: window.AGB_COLOR_PREF_OFF = true
 *
 * Backend seam: window.AGB_COLOR_PREF_URL (per store; defaults to the
 * fullstack color-pref service). EU/IL override — see /agilite-pilot-port §4.
 */
(function () {
  'use strict';

  if (window.AGB_COLOR_PREF_OFF) return;

  var ENDPOINT = window.AGB_COLOR_PREF_URL ||
    'https://color-pref-432657747828.europe-west1.run.app/color-pref';
  var CACHE_KEY = 'SE_colorPref';
  var TTL_MS = 6 * 60 * 60 * 1000;

  function readVisitorId() {
    try {
      if (window.SE_visitorIdentity && window.SE_visitorIdentity.visitor_id) {
        return window.SE_visitorIdentity.visitor_id;
      }
      return localStorage.getItem('viewerId') ||
             localStorage.getItem('visitorID') || null;
    } catch (e) { return null; }
  }

  function loadCache(vid) {
    try {
      var c = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
      if (!c || c.vid !== vid) return null;
      if (!c.fetchedAt || (Date.now() - c.fetchedAt) > TTL_MS) return null;
      return c.answer || null;
    } catch (e) { return null; }
  }

  function saveCache(vid, answer) {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify({
        vid: vid, fetchedAt: Date.now(), answer: answer
      }));
    } catch (e) {}
  }

  function publish(colors, source) {
    window.SE_colorPref = { colors: colors || [], source: source };
    try {
      window.dispatchEvent(new CustomEvent('SE_colorPrefReady', {
        detail: window.SE_colorPref
      }));
    } catch (e) {}
  }

  // The theme's block picker renders the Color option as radio inputs whose
  // VALUE is the option value. Matching on value keeps this independent of
  // markup details; scoping to the picker keeps it off unrelated radios.
  function selectColour(colors, attempt) {
    if (window.__colorPrefApplied) return;
    var inputs = document.querySelectorAll(
      'variant-picker input[type="radio"], .variant-picker input[type="radio"]');
    if (!inputs.length) {
      if (attempt < 8) setTimeout(function () { selectColour(colors, attempt + 1); }, attempt * 300 + 200);
      return;
    }
    // the shopper got there first — their touch wins, forever
    for (var i = 0; i < inputs.length; i++) {
      if (inputs[i].dataset.colorPrefSeen) return;
      inputs[i].dataset.colorPrefSeen = '1';
    }
    for (var c = 0; c < colors.length; c++) {
      var want = String(colors[c]).trim().toLowerCase();
      for (var j = 0; j < inputs.length; j++) {
        var input = inputs[j];
        if (String(input.value).trim().toLowerCase() !== want) continue;
        if (input.checked) { window.__colorPrefApplied = colors[c]; return; }
        if (input.disabled) continue;                 // sold out: next colour
        commitClick(input);                           // click + verify the theme heard it
        window.__colorPrefApplied = colors[c];
        return;
      }
    }
  }

  // The size the shopper BOUGHT, selected AFTER the colour: the theme
  // re-renders the picker on variant:change, so this is its own retry pass
  // with its own marker. Size values are matched the same way colours are -
  // by radio value - but only radios that do NOT belong to the colour set,
  // so "L" can never accidentally claim a colour swatch.
  function selectSize(size, knownColors, attempt) {
    if (window.__sizePrefApplied) return;
    var want = String(size).trim().toLowerCase();
    var colorSet = {};
    (knownColors || []).forEach(function (c) { colorSet[String(c).trim().toLowerCase()] = 1; });
    var inputs = document.querySelectorAll(
      'variant-picker input[type="radio"], .variant-picker input[type="radio"]');
    if (!inputs.length) {
      if (attempt < 8) setTimeout(function () { selectSize(size, knownColors, attempt + 1); }, attempt * 300 + 200);
      return;
    }
    for (var j = 0; j < inputs.length; j++) {
      var input = inputs[j];
      var val = String(input.value).trim().toLowerCase();
      if (val !== want || colorSet[val]) continue;
      if (input.dataset.sizePrefSeen) return;   // the shopper touched sizes
      if (input.checked) { window.__sizePrefApplied = size; return; }
      if (input.disabled) continue;             // that size sold out
      input.dataset.sizePrefSeen = '1';
      commitClick(input);
      window.__sizePrefApplied = size;
      return;
    }
    // picker may not have rendered this option group yet
    if (attempt < 8) setTimeout(function () { selectSize(size, knownColors, attempt + 1); }, attempt * 300 + 200);
  }

  function pageHandle() {
    var m = location.pathname.match(/\/products\/([a-z0-9-]+)/);
    return m ? m[1] : null;
  }

  // A synthetic click can land BEFORE the theme's variant-picker has
  // hydrated its listeners: the radio toggles, the swatch looks selected,
  // and the gallery/price/SKU stay on the old variant (seen live on the
  // Battleshell PDP, 2026-08-24 - Black checked, page still Multicam).
  // So: click, then VERIFY the form's variant id moved; if the theme did
  // not react, re-dispatch bubbling events, and re-verify once more.
  // The ladder above ended in a click that was never checked: three attempts,
  // and if the third also failed nothing looked again. Worse, every attempt
  // sets `checked` whether or not the theme follows - so a page that refuses
  // to move ends up with the FRAME around Ranger Green while the label, the
  // price, the SKU and the pictures all still say Multicam (Dorian 2026-08-31,
  // with the screenshot: 8130MTCSML at $184.90 under a green swatch). We were
  // manufacturing the inconsistency we were trying to repair.
  //
  // So: keep asking until the FORM moves, which is the only witness that the
  // theme actually changed variant, and back off between tries because the
  // failure is the picker not having hydrated yet. If it never moves, put the
  // picker back where the page really is - a swatch claiming a colour the
  // page is not on is worse than no preference at all.
  function commitClick(input) {
    var form = input.closest('form') || document.querySelector('form[action*="/cart/add"]');
    var idEl = form && form.querySelector('input[name="id"], select[name="id"]');
    if (!idEl) { input.click(); return; }
    var before = String(idEl.value);
    var group = [];
    try {
      group = [].slice.call((form || document).querySelectorAll(
        'input[name="' + input.name + '"]'));
    } catch (e) { group = []; }
    var wasChecked = group.filter(function (r) { return r.checked; })[0] || null;

    var tries = 0;
    function moved() { return String(idEl.value) !== before; }

    function attempt() {
      if (moved()) return;                       // the theme took it
      if (tries >= 6) {                          // ~4s of asking; it is not coming
        if (!moved() && wasChecked && wasChecked !== input) {
          input.checked = false;
          wasChecked.checked = true;             // say what the page actually is
        }
        return;
      }
      tries++;
      if (tries > 1) {
        input.checked = true;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      input.click();
      setTimeout(attempt, 150 * tries);          // 150,300,450,600,750,900
    }
    attempt();
  }

  function whenPickerReady(cb) {
    // prefer the honest signal (custom element defined), fall back to time
    try {
      if (window.customElements && customElements.get && !customElements.get('variant-picker') &&
          document.querySelector('variant-picker')) {
        var done = false;
        customElements.whenDefined('variant-picker').then(function () {
          if (!done) { done = true; cb(); }
        });
        setTimeout(function () { if (!done) { done = true; cb(); } }, 2500);
        return;
      }
    } catch (e) { /* fall through */ }
    cb();
  }

  function apply(answer, source) {
    if (!answer || !answer.known) return;
    var colors = (answer.colors || []).slice();
    // the variant they BOUGHT of the product on screen outranks preference:
    // a shopper looking at their own gear expects to see it as they own it
    var owned = null;
    var handle = pageHandle();
    if (handle && answer.owned && answer.owned[handle]) {
      owned = answer.owned[handle];
      if (owned.color) colors.unshift(owned.color);
    }
    if (!colors.length && !owned) return;
    publish(colors, source);
    // a link that names a variant is a deliberate choice
    if (/[?&]variant=/.test(location.search)) return;
    if (!document.querySelector('form[action*="/cart/add"]')) return;   // not a PDP
    whenPickerReady(function () {
      if (colors.length) selectColour(colors, 0);
      if (owned && owned.size) {
        setTimeout(function () { selectSize(owned.size, colors, 0); }, 500);
      }
    });
  }

  function start() {
    var vid = readVisitorId();
    if (!vid) return;
    var cached = loadCache(vid);
    if (cached) { apply(cached, 'cache'); return; }
    fetch(ENDPOINT + '?vid=' + encodeURIComponent(vid), { mode: 'cors' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (answer) {
        if (!answer) return;
        saveCache(vid, answer);
        apply(answer, 'network');
      })
      .catch(function () {});
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
